import React, { useEffect, useState, useRef } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap, Circle } from "react-leaflet";
import L from "leaflet";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { AlertTriangle, Briefcase, Footprints, MapPin, X, ShieldAlert, Camera, ThumbsUp, CheckCircle2, LocateFixed, Loader2, Navigation, Phone, Stethoscope, Sliders, TreePine, HeartHandshake, Trash2, Zap, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { hapticTap } from "@/lib/haptics";
import QuickHazardCapture from "@/components/QuickHazardCapture";
import LivePresencePanel from "@/components/LivePresencePanel";
import LivePresenceModal from "@/components/LivePresenceModal";
import RadarLayer from "@/components/RadarLayer";
import RadarToggle, { useRadarPrefs, radarPrefsToLayerConfig } from "@/components/RadarToggle";

const COLORS = { emergency: "#FF3B30", mission: "#FFB020", hazard: "#FF3B30", dog_park: "#8b5cf6", vet: "#06b6d4" };

const HAZARD_TYPES = {
  poison_bait:   { label: "Bocconi avvelenati",       color: "#FF3B30", icon: "☠" },
  dangerous_dog: { label: "Cane sciolto pericoloso",  color: "#FF3B30", icon: "⚠" },
  glass:         { label: "Vetri / oggetti taglienti",color: "#FFB020", icon: "✦" },
  chemical:      { label: "Sostanze tossiche",        color: "#FF3B30", icon: "🧪" },
  wild_animal:   { label: "Animale selvatico",        color: "#FFB020", icon: "🦊" },
  other:         { label: "Altro pericolo",           color: "#FFB020", icon: "!" },
};

function makeIcon(color, pulsing) {
  const ringHtml = pulsing
    ? `<span class="absolute inset-0 rounded-full" style="background:${color};opacity:0.6;animation:orio-ping 1.6s cubic-bezier(0,0,0.2,1) infinite"></span>`
    : "";
  const html = `
    <div style="position:relative;width:24px;height:24px;display:flex;align-items:center;justify-content:center;">
      ${ringHtml}
      <span style="position:relative;width:14px;height:14px;border-radius:9999px;background:${color};box-shadow:0 0 0 3px rgba(0,0,0,0.6),0 0 12px ${color};"></span>
    </div>`;
  return L.divIcon({ html, className: "orio-leaflet-icon", iconSize: [24, 24], iconAnchor: [12, 12] });
}

function makePoiIcon(type) {
  const color = type === "dog_park" ? COLORS.dog_park : COLORS.vet;
  const symbol = type === "dog_park" ? "🐕" : "+";
  const html = `
    <div style="position:relative;width:34px;height:34px;display:flex;align-items:center;justify-content:center;">
      <span style="position:absolute;inset:5px;border-radius:9999px;background:${color};opacity:0.35;animation:orio-ping 2.4s cubic-bezier(0,0,0.2,1) infinite"></span>
      <span style="position:relative;width:24px;height:24px;display:flex;align-items:center;justify-content:center;
                  background:${color}; border:2px solid #FFFFFF; border-radius:50%; font-size:12px; font-weight:bold;
                  color:white; box-shadow:0 0 0 1px ${color}80, 0 4px 14px ${color}66, 0 2px 8px rgba(0,0,0,0.35);">
        ${symbol}
      </span>
    </div>`;
  return L.divIcon({ html, className: "orio-poi-icon", iconSize: [34, 34], iconAnchor: [17, 17] });
}

function makeHazardIcon(hType, color) {
  const symbol = HAZARD_TYPES[hType]?.icon || "!";
  const c = color || "#FF3B30";
  const html = `
    <div style="position:relative;width:36px;height:36px;display:flex;align-items:flex-end;justify-content:center;">
      <span style="position:absolute;left:50%;bottom:4px;width:28px;height:28px;margin-left:-14px;border-radius:9999px;background:${c};opacity:0.40;animation:orio-ping 1.8s cubic-bezier(0,0,0.2,1) infinite"></span>
      <div style="position:relative;width:26px;height:26px;display:flex;align-items:center;justify-content:center;
                  background:${c}; border:2px solid #FFFFFF;
                  clip-path: polygon(50% 0%, 100% 100%, 0% 100%);
                  color:white; font-size:13px; font-weight:bold;
                  filter: drop-shadow(0 3px 8px ${c}80) drop-shadow(0 2px 4px rgba(0,0,0,0.4));">
        <span style="position:relative;top:5px;">${symbol}</span>
      </div>
    </div>`;
  return L.divIcon({ html, className: "orio-hazard-icon", iconSize: [36, 36], iconAnchor: [18, 32] });
}

function makeUserPosIcon() {
  const html = `
    <div style="position:relative;width:30px;height:30px;display:flex;align-items:center;justify-content:center;">
      <span style="position:absolute;inset:0;border-radius:9999px;background:#3b82f6;opacity:0.30;animation:orio-ping 2s cubic-bezier(0,0,0.2,1) infinite"></span>
      <span style="position:relative;width:14px;height:14px;border-radius:9999px;background:#3b82f6;
                   box-shadow:0 0 0 3px rgba(255,255,255,0.95), 0 0 0 5px rgba(59,130,246,0.45), 0 2px 8px rgba(0,0,0,0.5);"></span>
    </div>`;
  return L.divIcon({ html, className: "orio-user-icon", iconSize: [30, 30], iconAnchor: [15, 15] });
}

/** Online helper inside an active SOS radius — green halo + photo or paw. */
function makeHelperIcon(photoUrl) {
  const inner = photoUrl
    ? `<img src="${photoUrl}" alt="" style="width:100%;height:100%;object-fit:cover"/>`
    : `<div style="width:100%;height:100%;display:flex;align-items:center;justify-content:center;background:linear-gradient(135deg,#34D399,#2DBA60);color:#fff;font-size:13px;font-weight:700">🐾</div>`;
  const html = `
    <div style="position:relative;width:40px;height:40px;display:flex;align-items:center;justify-content:center;">
      <span style="position:absolute;inset:-3px;border-radius:9999px;background:#2DBA60;opacity:0.30;animation:orio-ping 2.6s cubic-bezier(0,0,0.2,1) infinite"></span>
      <div style="position:relative;width:34px;height:34px;border-radius:9999px;border:3px solid #fff;background:#fff;overflow:hidden;
                  box-shadow:0 0 0 2px #2DBA60, 0 6px 14px -4px rgba(45,186,96,0.55), 0 2px 6px rgba(0,0,0,0.25);">
        ${inner}
      </div>
    </div>`;
  return L.divIcon({ html, className: "orio-helper-icon", iconSize: [40, 40], iconAnchor: [20, 20] });
}

/** Online + visible ORIO user (Live Presence). Green ring + photo/initials. */
function makeLivePeerIcon(photoUrl, name) {
  const initials = (name || "?").split(" ").slice(0, 2).map((w) => w[0] || "").join("").toUpperCase();
  const inner = photoUrl
    ? `<img src="${photoUrl}" alt="" style="width:100%;height:100%;object-fit:cover"/>`
    : `<div style="width:100%;height:100%;display:flex;align-items:center;justify-content:center;background:linear-gradient(135deg,#34D399,#2DBA60);color:#fff;font-size:11px;font-weight:700">${initials}</div>`;
  const html = `
    <div style="position:relative;width:38px;height:38px;display:flex;align-items:center;justify-content:center;">
      <span style="position:absolute;inset:-2px;border-radius:9999px;background:#2DBA60;opacity:0.28;animation:orio-ping 2.6s cubic-bezier(0,0,0.2,1) infinite"></span>
      <div style="position:relative;width:32px;height:32px;border-radius:9999px;border:2px solid #fff;background:#fff;overflow:hidden;
                  box-shadow:0 0 0 2px #2DBA60, 0 6px 14px -4px rgba(45,186,96,0.45), 0 2px 6px rgba(0,0,0,0.22);">
        ${inner}
      </div>
    </div>`;
  return L.divIcon({ html, className: "orio-live-peer-icon", iconSize: [38, 38], iconAnchor: [19, 19] });
}

/** Shelter marker — small warm heart pin with dog count badge */
function makeShelterIcon(dogsCount) {
  const badge = dogsCount > 0
    ? `<span style="position:absolute;top:-4px;right:-4px;min-width:18px;height:18px;padding:0 4px;border-radius:9999px;background:#FF7A1A;color:#fff;font-size:10px;font-weight:800;display:flex;align-items:center;justify-content:center;border:2px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,0.2);">${dogsCount}</span>`
    : "";
  const html = `
    <div style="position:relative;width:40px;height:40px;display:flex;align-items:center;justify-content:center;">
      <div style="width:36px;height:36px;border-radius:12px;background:linear-gradient(135deg,#FFE8CB,#F4B83C);border:2px solid #fff;display:flex;align-items:center;justify-content:center;box-shadow:0 6px 14px -4px rgba(255,122,26,0.55);">
        <span style="font-size:18px;">🏠</span>
      </div>
      ${badge}
    </div>`;
  return L.divIcon({ html, className: "orio-shelter-icon", iconSize: [40, 40], iconAnchor: [20, 20] });
}

/** Open the destination in the system maps app (Apple on iOS, Google elsewhere). */
function openInMaps(lat, lng, label) {
  const isApple = /iPad|iPhone|iPod|Mac/.test(navigator.userAgent) && !window.MSStream;
  const q = `${lat},${lng}`;
  const url = isApple
    ? `https://maps.apple.com/?daddr=${q}${label ? `&q=${encodeURIComponent(label)}` : ""}`
    : `https://www.google.com/maps/dir/?api=1&destination=${q}`;
  window.open(url, "_blank", "noopener,noreferrer");
}

function NavigateBtn({ lat, lng, label, color = "#34D399", testid }) {
  return (
    <button
      data-testid={testid || "navigate-btn"}
      onClick={() => openInMaps(lat, lng, label)}
      className="mt-2 w-full inline-flex items-center justify-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full"
      style={{ background: `${color}22`, color, border: `1px solid ${color}55` }}
    >
      <Navigation className="h-3 w-3" /> Naviga
    </button>
  );
}

function FitBounds({ points }) {
  const map = useMap();
  const didFitRef = React.useRef(false);
  useEffect(() => {
    if (points.length === 0) return;
    // Fit-to-bounds ONLY on the first render after points appear.
    // Subsequent polling updates must NOT snap back — the user is in control.
    if (didFitRef.current) return;
    const bounds = L.latLngBounds(points.map((p) => [p.lat, p.lng]));
    map.fitBounds(bounds, { padding: [50, 50], maxZoom: 11 });
    didFitRef.current = true;
  }, [points, map]);
  return null;
}

function CaptureMap({ onMap }) {
  const map = useMap();
  useEffect(() => { onMap(map); }, [map, onMap]);
  return null;
}

export default function Dashboard() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [points, setPoints] = useState([]);
  const [pois, setPois] = useState([]);
  const [sosHelpers, setSosHelpers] = useState([]); // online+visible users inside my active SOS radius
  const [livePeers, setLivePeers] = useState([]); // online+visible users within 15km presence radius
  const [verifiedShelters, setVerifiedShelters] = useState([]);

  // Load verified shelters once (they change rarely — no polling needed)
  useEffect(() => {
    api.get("/shelter/verified").then(({ data }) => {
      setVerifiedShelters((data.items || []).filter((s) => s.lat && s.lng));
    }).catch(() => {});
  }, []);
  // Map opens "clean" — only the non-negotiable ORIO safety layers are on
  // (emergency + hazards). Everything else (missions, parks, vets) is
  // toggled by the user via the layer chips so the first paint stays fast
  // and the map isn't a wall of pins. Persisted per browser in localStorage
  // so users who curate their layers don't have to re-toggle on each visit.
  const [filters, setFilters] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem("orio_map_filters") || "null");
      if (saved && typeof saved === "object") {
        // Force safety layers on regardless of persisted state.
        return { ...saved, emergency: true, hazard: true };
      }
    } catch { /* ignore */ }
    return { emergency: true, mission: false, hazard: true, dog_park: false, vet: false };
  });

  // Persist user's chosen layers so the next opening remembers them.
  useEffect(() => {
    try { localStorage.setItem("orio_map_filters", JSON.stringify(filters)); } catch { /* ignore */ }
  }, [filters]);
  const [radarPrefs, setRadarPrefs] = useRadarPrefs();
  const [autoOpenRadar, setAutoOpenRadar] = useState(false);
  const [autoOpenPresence, setAutoOpenPresence] = useState(false);

  // If arriving with ?radar=on (e.g. from Home carousel slide), auto-enable all radar layers once.
  // If arriving with ?open=radar or ?open=presence, auto-open the respective modal.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("radar") === "on") {
      setRadarPrefs((p) => ({
        ...p, fires: true, weather: true,
        disease_leish: true, disease_filaria: true, disease_ticks: true,
        heat: true,
      }));
    }
    const openTarget = params.get("open");
    if (openTarget === "radar") setAutoOpenRadar(true);
    else if (openTarget === "presence") setAutoOpenPresence(true);
    // Clean the URL so params don't reactivate on every reload.
    if (params.get("radar") || openTarget) {
      const url = new URL(window.location.href);
      url.searchParams.delete("radar");
      url.searchParams.delete("open");
      window.history.replaceState({}, "", url.toString());
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [center, setCenter] = useState([41.9028, 12.4964]); // Roma default
  const [selectedDog, setSelectedDog] = useState(null);
  const [emergencyOpen, setEmergencyOpen] = useState(false);
  const [hazardOpen, setHazardOpen] = useState(false);
  const [quickHazardOpen, setQuickHazardOpen] = useState(false);
  const [mapRef, setMapRef] = useState(null);
  const [locating, setLocating] = useState(false);
  const [userPos, setUserPos] = useState(() => {
    // Seed from user's saved location if available so the radius filter works
    // immediately, even before live geolocate.
    const loc = user?.location;
    return loc?.lat && loc?.lng ? { lat: loc.lat, lng: loc.lng } : null;
  });
  const [followMe, setFollowMe] = useState(false);
  const watchIdRef = useRef(null);
  const [rangeOpen, setRangeOpen] = useState(false);
  const [savingRange, setSavingRange] = useState(false);
  const [localPrefs, setLocalPrefs] = useState(null);

  // Initialize local prefs from user
  useEffect(() => {
    if (user?.radius_prefs) setLocalPrefs(user.radius_prefs);
  }, [user]);

  // Full-height mobile map — neutralise AppShell's bottom padding while active
  useEffect(() => {
    document.body.classList.add("orio-map-active");
    return () => document.body.classList.remove("orio-map-active");
  }, []);

  const persistRange = async (next) => {
    setLocalPrefs(next);
    setSavingRange(true);
    try {
      await api.put("/users/me", { radius_prefs: next });
      await refresh();
    } catch {
      // silent — local state preserves it
    } finally {
      setSavingRange(false);
    }
  };

  // Start/stop continuous geolocation tracking
  const startWatch = () => {
    if (!navigator.geolocation || watchIdRef.current != null) return;
    watchIdRef.current = navigator.geolocation.watchPosition(
      (p) => {
        const loc = { lat: p.coords.latitude, lng: p.coords.longitude };
        setUserPos(loc);
        if (followMe && mapRef) mapRef.setView([loc.lat, loc.lng], mapRef.getZoom() || 14, { animate: true });
      },
      () => {/* silent — initial recenter already toasted on failure */},
      { enableHighAccuracy: true, maximumAge: 5000, timeout: 15000 }
    );
  };
  useEffect(() => {
    startWatch();
    return () => {
      if (watchIdRef.current != null && navigator.geolocation) {
        navigator.geolocation.clearWatch(watchIdRef.current);
        watchIdRef.current = null;
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapRef, followMe]);

  const recenter = () => {
    if (!navigator.geolocation) {
      toast.error("Geolocalizzazione non disponibile");
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (p) => {
        const loc = { lat: p.coords.latitude, lng: p.coords.longitude };
        setCenter([loc.lat, loc.lng]);
        setUserPos(loc);
        setFollowMe(true);
        if (mapRef) mapRef.flyTo([loc.lat, loc.lng], 14, { duration: 0.9 });
        api.post("/users/location", loc).catch(() => {});
        loadPOIs(loc.lat, loc.lng);
        setLocating(false);
      },
      () => { setLocating(false); toast.error("Posizione non disponibile"); },
      { timeout: 8000, enableHighAccuracy: true }
    );
  };

  // Find the nearest open vet right now and tel: it
  const callNearestVet = () => {
    const vets = (pois || []).filter((p) => p.type === "vet");
    if (!userPos) {
      toast.error("Attiva la geolocalizzazione prima");
      return;
    }
    if (vets.length === 0) {
      toast.error("Nessun veterinario trovato nelle vicinanze");
      return;
    }
    const withDist = vets
      .map((v) => ({ ...v, d: Math.hypot(v.lat - userPos.lat, v.lng - userPos.lng) }))
      .sort((a, b) => a.d - b.d);
    const target = withDist.find((v) => v.phone) || withDist[0];
    if (!target.phone) {
      toast("Veterinario senza telefono pubblico. Apro la mappa.", { icon: "ℹ" });
      openInMaps(target.lat, target.lng, target.name);
      return;
    }
    window.location.href = `tel:${target.phone.replace(/\s/g, "")}`;
  };

  const loadPoints = async () => {
    try {
      const { data } = await api.get("/map/points");
      setPoints(data);
    } catch (e) {
      console.error(e);
    }
  };

  const [poisLoading, setPoisLoading] = useState(false);

  const loadPOIs = async (lat, lng) => {
    // Resilient loader.
    // Step 1: initial cached call. If POIs, done.
    // Step 2: if empty, fire nocache=1 IN PARALLEL (fire-and-forget) — this
    //         kicks the backend into a synchronous Overpass fetch that
    //         populates the cache row. Meanwhile poll the cheap cached
    //         endpoint with backoff so the UI updates as soon as the row
    //         appears. Total poll window ~62s covers worst-case Overpass
    //         latency (49s observed on prod during peak).
    setPoisLoading(true);
    const fetchPois = async (extra = {}) => {
      try {
        const { data } = await api.get("/map/poi", {
          params: { lat, lng, radius_km: 25, ...extra },
          timeout: 60000,
        });
        return data.pois || [];
      } catch {
        return null;
      }
    };
    try {
      const first = await fetchPois();
      if (first && first.length > 0) { setPois(first); return; }
      // Fire-and-forget nocache — populates the cache in background even if
      // it 502s at the ingress. Backend still writes the cache row on success.
      fetchPois({ nocache: 1 }).catch(() => {});
      // Poll cached endpoint with expanding backoff (~62s total).
      const delays = [2000, 3000, 5000, 7000, 10000, 15000, 20000];
      for (const d of delays) {
        await new Promise((r) => setTimeout(r, d));
        const next = await fetchPois();
        if (next && next.length > 0) { setPois(next); return; }
      }
      // Give up gracefully — keep empty state; user can pull-to-refresh.
      setPois([]);
    } finally {
      setPoisLoading(false);
    }
  };

  useEffect(() => {
    loadPoints();
    const i = setInterval(loadPoints, 15000);
    // Refresh immediately when the PWA/tab becomes visible again
    const onVis = () => { if (document.visibilityState === "visible") loadPoints(); };
    document.addEventListener("visibilitychange", onVis);
    // Try to get location with fallback that ALWAYS loads POIs
    let loaded = false;
    const fallback = () => {
      if (loaded) return;
      loaded = true;
      loadPOIs(41.9028, 12.4964);
    };
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (p) => {
          if (loaded) return;
          loaded = true;
          const loc = { lat: p.coords.latitude, lng: p.coords.longitude };
          setCenter([loc.lat, loc.lng]);
          api.post("/users/location", loc).catch(() => {});
          loadPOIs(loc.lat, loc.lng);
        },
        fallback,
        { timeout: 6000, enableHighAccuracy: false }
      );
      // safety net
      setTimeout(fallback, 7000);
    } else {
      fallback();
    }
    return () => {
      clearInterval(i);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, []);

  // Apply user radius_prefs (distance from user position)
  const haversineKm = (a, b) => {
    const R = 6371;
    const toRad = (x) => (x * Math.PI) / 180;
    const dLat = toRad(b.lat - a.lat);
    const dLng = toRad(b.lng - a.lng);
    const aa = Math.sin(dLat/2)**2 + Math.cos(toRad(a.lat))*Math.cos(toRad(b.lat))*Math.sin(dLng/2)**2;
    return 2 * R * Math.asin(Math.sqrt(aa));
  };
  const prefs = localPrefs || user?.radius_prefs || { missions: 25, vet: 25, parks: 15, hazards: 25 };
  const filtered = points.filter((p) => {
    if (!filters[p.type]) return false;
    if (!userPos) return true; // when we don't know user position, show everything
    const d = haversineKm(userPos, p);
    if (p.type === "emergency") return true; // emergencies always show
    if (p.type === "hazard") return d <= prefs.hazards;
    if (p.type === "mission") return d <= prefs.missions;
    return true;
  });
  const filteredPois = pois.filter((p) => {
    if (!filters[p.type]) return false;
    // NOTE: the backend already scopes POIs to a 25 km radius around the
    // requested lat/lng, so we intentionally DO NOT filter again here.
    // Applying the user's `radius_prefs` on top produced 0 results when the
    // saved profile location differed from the current live geolocation.
    return true;
  });

  // Counts shown on the FilterChip pills — MUST match what will be rendered
  // on the map (i.e. after distance filtering) so users don't see "4 missioni"
  // and then zero markers when they toggle the layer. We count regardless of
  // the toggle state so the chip always shows "how many are near you".
  const inRadius = (type) => {
    return points.filter((p) => {
      if (p.type !== type) return false;
      if (type === "emergency") return true;
      if (!userPos) return true;
      const d = haversineKm(userPos, p);
      if (type === "hazard") return d <= prefs.hazards;
      if (type === "mission") return d <= prefs.missions;
      return true;
    }).length;
  };
  const activeEmergencies = points.filter((p) => p.type === "emergency");
  // My own SOS (if any) — used to surface online helpers within the alert radius
  const myEmergency = user?.id
    ? activeEmergencies.find((e) => e.owner_id === user.id)
    : null;

  // Poll online+visible users inside MY SOS radius. Only active while I have a live SOS.
  useEffect(() => {
    if (!myEmergency) { setSosHelpers([]); return; }
    let cancelled = false;
    const load = async () => {
      try {
        const r = await api.get("/missioni/visible-users", {
          params: {
            lat: myEmergency.lat,
            lng: myEmergency.lng,
            radius_km: myEmergency.sos_radius_km || 25,
          },
        });
        if (!cancelled) setSosHelpers(r.data?.items || []);
      } catch { /* silent */ }
    };
    load();
    const t = setInterval(load, 30_000);
    return () => { cancelled = true; clearInterval(t); };
  }, [myEmergency?.id, myEmergency?.sos_radius_km, myEmergency?.lat, myEmergency?.lng]);

  return (
    <div className="relative w-full flex-1 flex flex-col min-h-0">
      {/* Map */}
      <MapContainer
        center={center}
        zoom={6}
        scrollWheelZoom
        style={{ flex: "1 1 auto", width: "100%", minHeight: 0 }}
        zoomControl={false}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
          maxZoom={19}
          keepBuffer={6}
          updateWhenIdle={false}
          updateWhenZooming={false}
        />
        <FitBounds points={filtered} />
        <CaptureMap onMap={setMapRef} />
        <RadarLayer enabled={radarPrefsToLayerConfig(radarPrefs)} />
        {userPos && (
          <Marker
            position={[userPos.lat, userPos.lng]}
            icon={makeUserPosIcon()}
            zIndexOffset={500}
            interactive={false}
          />
        )}
        {/* SOS dynamic radius circles around active emergencies */}
        {activeEmergencies.map((e) => {
          const r = (e.sos_radius_km || 25) * 1000; // meters
          return (
            <React.Fragment key={`sos-${e.id}`}>
              <Circle
                center={[e.lat, e.lng]}
                radius={r}
                pathOptions={{
                  color: "#FF3B30",
                  fillColor: "#FF3B30",
                  fillOpacity: 0.10,
                  weight: 2,
                  dashArray: "6 8",
                }}
                interactive={false}
              />
              <Circle
                center={[e.lat, e.lng]}
                radius={r * 0.6}
                pathOptions={{
                  color: "#FF3B30",
                  fillColor: "#FF3B30",
                  fillOpacity: 0.06,
                  weight: 1,
                  opacity: 0.5,
                }}
                interactive={false}
              />
            </React.Fragment>
          );
        })}
        {/* SOS Helpers — online + visible users inside MY active SOS radius */}
        {myEmergency && sosHelpers.map((h) => (
          <Marker
            key={`sos-helper-${h.id}`}
            position={[h.lat, h.lng]}
            icon={makeHelperIcon(h.photo_url)}
            zIndexOffset={700}
          >
            <Popup>
              <div className="text-[#1A1A1A] min-w-[180px]">
                <div className="text-[10px] uppercase tracking-widest font-bold text-[#2DBA60]">
                  🟢 Online · nel raggio SOS
                </div>
                <div className="font-display text-base mt-0.5">{h.name || "Padrone ORIO"}</div>
                <div className="text-xs text-[#6B6B6B] mt-0.5">{h.distance_km} km dal tuo SOS</div>
              </div>
            </Popup>
          </Marker>
        ))}
        {/* HAZARD HEATMAP — soft gradient circles around each hazard (toggled by filter) */}
        {filters.hazard && filtered.filter((p) => p.type === "hazard").map((h) => (
          <React.Fragment key={`heat-${h.id}`}>
            <Circle
              center={[h.lat, h.lng]}
              radius={300}
              pathOptions={{
                color: h.color || "#FF3B30",
                fillColor: h.color || "#FF3B30",
                fillOpacity: 0.10,
                weight: 0,
              }}
              interactive={false}
            />
            <Circle
              center={[h.lat, h.lng]}
              radius={150}
              pathOptions={{
                color: h.color || "#FF3B30",
                fillColor: h.color || "#FF3B30",
                fillOpacity: 0.18,
                weight: 0,
              }}
              interactive={false}
            />
          </React.Fragment>
        ))}
        {filtered.map((p) => (
          <Marker
            key={`${p.type}-${p.id}`}
            position={[p.lat, p.lng]}
            icon={p.type === "hazard"
              ? makeHazardIcon(p.hazard_type, p.color)
              : makeIcon(p.type === "emergency" ? COLORS.emergency : COLORS.mission, p.type === "emergency")}
          >
            <Popup>
              <div className="text-[#1A1A1A]">
                <div className="text-xs uppercase tracking-widest mb-1"
                  style={{ color: p.type === "emergency" ? COLORS.emergency : p.type === "hazard" ? (p.color || COLORS.hazard) : COLORS.mission }}>
                  {p.type === "emergency" ? "Emergenza"
                    : p.type === "hazard" ? `Pericolo: ${p.title}`
                    : "Missione"}
                </div>
                <div className="font-display text-lg">{p.type === "hazard" ? p.subtitle : p.title}</div>
                {p.type === "hazard" && p.photo_url && (
                  <img src={p.photo_url} alt="" className="mt-2 rounded-lg max-h-40 w-full object-cover" />
                )}
                {p.type !== "hazard" && p.subtitle && (
                  <div className="text-sm text-[#6B6B6B] mt-1">{p.subtitle}</div>
                )}
                {p.type === "hazard" && (
                  <HazardPopupActions hazard={p} reload={loadPoints} />
                )}
                {p.type === "emergency" && (
                  <Link to={`/passport/${p.dog_id}`} className="mt-3 inline-block text-sm underline text-[#FF3B30]">
                    Apri passaporto →
                  </Link>
                )}
                {p.type === "mission" && p.id && (
                  <Link to={`/app/missioni/${p.id}`} className="mt-3 inline-block text-sm underline text-[#FFB020]">
                    Apri dossier missione →
                  </Link>
                )}
                {p.type !== "hazard" && (
                  <NavigateBtn
                    lat={p.lat} lng={p.lng}
                    label={p.title || p.subtitle}
                    color={p.type === "emergency" ? COLORS.emergency : COLORS.mission}
                    testid={`nav-${p.type}-${p.id}`}
                  />
                )}
              </div>
            </Popup>
          </Marker>
        ))}
        {filteredPois.map((p) => (
          <Marker key={`poi-${p.id}`} position={[p.lat, p.lng]} icon={makePoiIcon(p.type)}>
            <Popup>
              <div className="text-[#1A1A1A] min-w-[180px]">
                <div className="text-xs uppercase tracking-widest mb-1"
                  style={{ color: p.type === "dog_park" ? COLORS.dog_park : COLORS.vet }}>
                  {p.type === "dog_park" ? "Area cani" : "Veterinario"}
                </div>
                <div className="font-display text-base">{p.name}</div>
                {p.address && <div className="text-xs text-[#6B6B6B] mt-1">{p.address}</div>}
                {p.opening_hours && <div className="text-xs text-[#9A9A9A] mt-1">⏰ {p.opening_hours}</div>}
                {p.phone && (
                  <a href={`tel:${p.phone.replace(/\s/g,"")}`}
                    className="mt-2 w-full inline-flex items-center justify-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full"
                    style={{ background: "#34D39922", color: "#34D399", border: "1px solid #34D39955" }}>
                    <Phone className="h-3 w-3" /> {p.phone}
                  </a>
                )}
                {p.website && <a href={p.website} target="_blank" rel="noopener noreferrer" className="block text-xs underline text-[#34D399] mt-1">Sito web →</a>}
                <NavigateBtn
                  lat={p.lat} lng={p.lng}
                  label={p.name}
                  color={p.type === "dog_park" ? COLORS.dog_park : COLORS.vet}
                  testid={`nav-poi-${p.id}`}
                />
              </div>
            </Popup>
          </Marker>
        ))}
        {/* Live presence — online + visible peers within 15km */}
        {livePeers.map((u) => (
          <Marker
            key={`peer-${u.id}`}
            position={[u.lat, u.lng]}
            icon={makeLivePeerIcon(u.photo_url, u.name)}
            zIndexOffset={200}
          >
            <Popup>
              <div className="text-[#1A1A1A] min-w-[180px]">
                <div className="text-[10px] uppercase tracking-widest text-[#2DBA60] mb-1">🟢 Online</div>
                <div className="font-display text-base leading-tight">{u.name || "Utente ORIO"}</div>
                {u.dog?.name && (
                  <div className="text-[11px] text-[#6B6B6B] mt-0.5">🐾 {u.dog.name}{u.dog.breed ? ` · ${u.dog.breed}` : ""}</div>
                )}
                <div className="text-[11px] text-[#9A9A9A] mt-1">{u.distance_km} km da te</div>
              </div>
            </Popup>
          </Marker>
        ))}
        {/* Verified shelters — warm heart pin with dog count badge */}
        {verifiedShelters.map((s) => (
          <Marker
            key={`shelter-${s.id}`}
            position={[s.lat, s.lng]}
            icon={makeShelterIcon(s.dogs_count || 0)}
            zIndexOffset={150}
          >
            <Popup>
              <div className="text-[#1A1A1A] min-w-[220px] max-w-[260px]">
                <div className="text-[10px] uppercase tracking-widest text-[#C25612] mb-1 font-semibold">🏠 Rifugio verificato</div>
                <div className="font-display text-base leading-tight">{s.name}</div>
                <div className="text-[11px] text-[#7A7A7A] mt-0.5">{s.city}</div>
                {(s.dogs || []).length > 0 && (
                  <div className="mt-2 pt-2 border-t border-[#F0EBE3]">
                    <div className="text-[10px] uppercase tracking-wider text-[#9A9A9A] font-semibold mb-1">
                      {s.dogs.length} in adozione
                    </div>
                    <div className="flex gap-1 flex-wrap">
                      {s.dogs.slice(0, 4).map((d) => (
                        <div key={d.id} className="flex flex-col items-center w-14">
                          <div className="h-10 w-10 rounded-lg overflow-hidden bg-[#F5EFE6] border border-[#F0EBE3]">
                            {d.photo_url
                              ? <img src={d.photo_url} alt={d.name} className="w-full h-full object-cover" />
                              : <div className="w-full h-full flex items-center justify-center text-[#F4B83C]">🐾</div>}
                          </div>
                          <div className="text-[10px] font-semibold text-[#1A1A1A] mt-0.5 truncate w-full text-center">{d.name}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <a
                  href={`/app/rifugio/${s.id}`}
                  className="mt-2.5 inline-flex items-center gap-1 justify-center w-full px-3 py-2 rounded-full text-[12px] font-bold text-white"
                  style={{ background: "linear-gradient(135deg, #FF7A1A 0%, #F4B83C 100%)" }}
                >
                  Vedi rifugio →
                </a>
              </div>
            </Popup>
          </Marker>
        ))}
      </MapContainer>

      {/* SOS Helpers banner — only when I have a live SOS */}
      {myEmergency && (
        <div
          className="absolute top-16 sm:top-20 left-1/2 -translate-x-1/2 z-[450] pointer-events-none flex flex-col items-center gap-2"
          data-testid="sos-helpers-banner"
        >
          <motion.div
            initial={{ y: -16, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            className="px-3 py-1.5 rounded-full bg-white/95 backdrop-blur border inline-flex items-center gap-2 text-[12px] font-semibold pointer-events-auto"
            style={{
              borderColor: "#2DBA6055",
              boxShadow: "0 10px 24px -10px rgba(0,0,0,0.18)",
            }}
          >
            <span className="h-2 w-2 rounded-full bg-[#2DBA60] animate-pulse" />
            {sosHelpers.length > 0
              ? <span><span className="text-[#1E7E3F]">{sosHelpers.length}</span> <span className="text-[#1A1A1A]">soccorritori online entro {myEmergency.sos_radius_km} km</span></span>
              : <span className="text-[#6B6B6B]">Nessun soccorritore visibile ora · raggio {myEmergency.sos_radius_km} km</span>
            }
          </motion.div>
          <motion.button
            data-testid="sos-cancel-btn"
            initial={{ y: -8, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.15 }}
            onClick={async () => {
              if (!window.confirm("Annullare l'SOS? Il cane è tornato a casa?")) return;
              try {
                await api.post(`/emergencies/${myEmergency.id}/close`);
                toast.success("SOS annullato — grazie a chi ha aiutato");
                setPoints((prev) => prev.filter((p) => p.id !== myEmergency.id));
                setSosHelpers([]);
              } catch (err) {
                toast.error(formatErr(err.response?.data?.detail) || "Errore annullamento SOS");
              }
            }}
            className="px-4 py-2 rounded-full text-[12px] font-semibold text-white inline-flex items-center gap-1.5 pointer-events-auto active:scale-[0.97] transition-transform"
            style={{
              background: "linear-gradient(135deg, #FF7A1A 0%, #FF5C2A 100%)",
              boxShadow: "0 10px 22px -8px rgba(255,92,42,0.55)",
            }}
          >
            <X className="h-3.5 w-3.5" />
            Annulla SOS
          </motion.button>
        </div>
      )}

      {/* Filter chips - top with mobile padding */}
      <div className="absolute top-4 left-2 right-2 md:left-4 md:right-auto z-[400]">
        {/* Loading indicator when POI cache is empty and we're polling */}
        {poisLoading && pois.length === 0 && (
          <div
            data-testid="poi-loading-hint"
            className="mb-2 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/95 backdrop-blur border border-[#F0EBE3] text-[11.5px] text-[#7A7A7A] shadow-md"
          >
            <span className="h-2 w-2 rounded-full bg-[#FF7A1A] animate-pulse" />
            Sto cercando aree cani e veterinari qui vicino…
          </div>
        )}
        <div className="glass rounded-full p-1 sm:p-1.5 flex gap-0.5 sm:gap-1 items-center overflow-x-auto no-scrollbar">
          <FilterChip
            label="Emergenza"
            color={COLORS.emergency}
            count={inRadius("emergency")}
            active={filters.emergency}
            onClick={() => setFilters((f) => ({ ...f, emergency: !f.emergency }))}
            testid="filter-emergency"
            icon={AlertTriangle}
          />
          <FilterChip
            label="Missioni"
            color={COLORS.mission}
            count={inRadius("mission")}
            active={filters.mission}
            onClick={() => setFilters((f) => ({ ...f, mission: !f.mission }))}
            testid="filter-missions"
            icon={Briefcase}
          />
          <FilterChip
            label="Pericoli"
            color={COLORS.hazard}
            count={inRadius("hazard")}
            active={filters.hazard}
            onClick={() => setFilters((f) => ({ ...f, hazard: !f.hazard }))}
            testid="filter-hazard"
            icon={ShieldAlert}
          />
          <div className="h-5 w-px bg-[#F0EBE3] mx-1" />
          <FilterChip
            label="Aree cani"
            color={COLORS.dog_park}
            count={pois.filter((p) => p.type === "dog_park").length}
            active={filters.dog_park}
            onClick={() => setFilters((f) => ({ ...f, dog_park: !f.dog_park }))}
            testid="filter-dog-park"
            icon={MapPin}
          />
          <FilterChip
            label="Veterinari"
            color={COLORS.vet}
            count={pois.filter((p) => p.type === "vet").length}
            active={filters.vet}
            onClick={() => setFilters((f) => ({ ...f, vet: !f.vet }))}
            testid="filter-vet"
            icon={MapPin}
          />
          <div className="h-5 w-px bg-[#F0EBE3] mx-1" />
          <button
            data-testid="map-range-toggle"
            onClick={() => setRangeOpen((v) => !v)}
            aria-label="Imposta raggi"
            className={`shrink-0 inline-flex items-center justify-center rounded-full transition-all h-7 w-7 sm:h-8 sm:w-8 ${
              rangeOpen ? "bg-white text-black" : "bg-[#F4EFE6] hover:bg-[#F0EBE3] text-[#6B6B6B]"
            }`}
            title="Raggi d'azione"
          >
            <Sliders className="h-3.5 w-3.5" />
          </button>
        </div>

        {/* Range popover */}
        {rangeOpen && (
          <div
            data-testid="map-range-popover"
            className="mt-2 rounded-2xl border border-[#F0EBE3] p-4 max-w-sm"
            style={{
              background: "linear-gradient(180deg, rgba(22,22,24,0.94) 0%, rgba(12,12,14,0.96) 100%)",
              backdropFilter: "blur(24px) saturate(180%)",
              WebkitBackdropFilter: "blur(24px) saturate(180%)",
            }}
          >
            <div className="flex items-start justify-between mb-2">
              <div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-[#FFB020]">Raggio d&apos;azione</div>
                <div className="font-display text-sm mt-0.5">Cosa vedere sulla mappa</div>
              </div>
              <button onClick={() => setRangeOpen(false)} className="h-7 w-7 rounded-full bg-[#F4EFE6] hover:bg-[#F0EBE3] flex items-center justify-center">
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
            <div className="space-y-3 mt-3">
              {[
                { key: "missions", label: "Missioni",    color: "#FFB020", icon: HeartHandshake },
                { key: "vet",      label: "Veterinari",  color: "#06b6d4", icon: Stethoscope },
                { key: "parks",    label: "Aree cani",   color: "#8b5cf6", icon: TreePine },
                { key: "hazards",  label: "Pericoli",    color: "#FF3B30", icon: ShieldAlert },
              ].map((r) => {
                const Icon = r.icon;
                const v = prefs[r.key] || 25;
                const pct = Math.round(((v - 5) / 95) * 100);
                return (
                  <div key={r.key} className="flex items-center gap-2.5" data-testid={`map-rad-${r.key}`}>
                    <div className="h-7 w-7 rounded-lg flex items-center justify-center shrink-0"
                      style={{ background: `${r.color}1F`, color: r.color, border: `1px solid ${r.color}33` }}>
                      <Icon className="h-3.5 w-3.5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline justify-between">
                        <div className="text-[12px] text-[#1A1A1A]">{r.label}</div>
                        <div className="text-[11px] font-mono tabular-nums" style={{ color: r.color }}>{v} km</div>
                      </div>
                      <input
                        type="range" min={5} max={100} step={5}
                        value={v}
                        onChange={(e) => persistRange({ ...prefs, [r.key]: parseInt(e.target.value, 10) })}
                        data-testid={`map-rad-${r.key}-input`}
                        className="w-full h-1 rounded-full appearance-none cursor-pointer focus:outline-none mt-1"
                        style={{
                          background: `linear-gradient(to right, ${r.color} 0%, ${r.color} ${pct}%, rgba(255,255,255,0.10) ${pct}%, rgba(255,255,255,0.10) 100%)`,
                        }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="text-[10px] text-[#9A9A9A] mt-3">
              {savingRange ? "Salvataggio…" : "I cambi sono salvati nel tuo profilo"}
            </div>
          </div>
        )}
      </div>

      {/* Geolocate button — top-right (avoids bottom-right Emergent badge) */}
      <button
        data-testid="map-geolocate"
        onClick={recenter}
        aria-label="Centra sulla mia posizione"
        className="absolute top-20 right-3 md:right-4 z-[600] h-12 w-12 rounded-full flex items-center justify-center transition-all"
        style={{
          background: followMe ? "rgba(59,130,246,0.22)" : "rgba(20,20,22,0.55)",
          backdropFilter: "blur(28px) saturate(180%)",
          WebkitBackdropFilter: "blur(28px) saturate(180%)",
          border: `1px solid ${followMe ? "rgba(59,130,246,0.55)" : "rgba(255,255,255,0.10)"}`,
          boxShadow: "0 10px 28px rgba(0,0,0,0.55)",
        }}
      >
        {locating
          ? <Loader2 className="h-5 w-5 text-[#1A1A1A] animate-spin" />
          : <LocateFixed className="h-5 w-5" style={{ color: followMe ? "#60a5fa" : "rgba(255,255,255,0.92)" }} />}
      </button>

      {/* Vet hotline — directly below geolocate */}
      <button
        data-testid="vet-hotline"
        onClick={callNearestVet}
        aria-label="Chiama il veterinario più vicino"
        className="absolute top-[148px] right-3 md:right-4 z-[600] h-12 w-12 rounded-full flex items-center justify-center transition-all"
        style={{
          background: "rgba(6,182,212,0.22)",
          backdropFilter: "blur(28px) saturate(180%)",
          WebkitBackdropFilter: "blur(28px) saturate(180%)",
          border: "1px solid rgba(6,182,212,0.55)",
          boxShadow: "0 10px 28px rgba(0,0,0,0.55)",
        }}
      >
        <Stethoscope className="h-5 w-5" style={{ color: "#06b6d4" }} />
      </button>

      {/* ORIO Radar — floating pill under Vet hotline. Opens layer toggle panel. */}
      <div className="absolute top-[212px] right-3 md:right-4 z-[600]" style={{ position: "absolute" }}>
        <RadarToggle prefs={radarPrefs} setPrefs={setRadarPrefs} defaultOpen={autoOpenRadar} />
      </div>

      {/* LIVE PRESENCE — moved into unified action menu (below) */}

      {/* UNIFIED ACTION FAB — one button opens a small floating menu.
          Only Vet hotline + GPS remain permanently outside (top-right stack). */}
      <MapActionsFab
        onSegnala={() => setQuickHazardOpen(true)}
        onEmergenza={() => setEmergencyOpen(true)}
        me={userPos}
        onPeersChange={setLivePeers}
        defaultPresenceOpen={autoOpenPresence}
      />

      {/* Trial/Pro banner is handled by AppShell's <TrialBanner /> — no dashboard-local banner needed */}

      {/* Dialogs */}
      <AnimatePresence>
        {emergencyOpen && (
          <EmergencyDialog
            key="em-dialog"
            onClose={() => setEmergencyOpen(false)}
            onDone={() => { setEmergencyOpen(false); loadPoints(); }}
          />
        )}
      </AnimatePresence>
      <AnimatePresence>
        {hazardOpen && (
          <HazardDialog
            key="hz-dialog"
            onClose={() => setHazardOpen(false)}
            onDone={() => { setHazardOpen(false); loadPoints(); }}
          />
        )}
      </AnimatePresence>

      {/* Quick 1-tap hazard capture (camera-first flow) */}
      <QuickHazardCapture
        open={quickHazardOpen}
        onClose={() => setQuickHazardOpen(false)}
        onDone={() => { setQuickHazardOpen(false); loadPoints(); }}
      />
    </div>
  );
}

function FilterChip({ label, color, count, active, onClick, testid, icon: Icon }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`px-3.5 py-2 rounded-full flex items-center gap-2 text-xs font-medium transition-all ${
        active ? "bg-[#F0EBE3] text-[#1A1A1A]" : "text-[#9A9A9A] hover:text-[#1A1A1A]"
      }`}
    >
      <Icon className="h-3.5 w-3.5" style={{ color }} />
      {label}
      <span className="px-1.5 py-0.5 rounded-full bg-[#F0EBE3] text-[10px]">{count}</span>
    </button>
  );
}

function Modal({ onClose, children, title, accent }) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[1000] bg-[#FBF7F1] backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ y: 30, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 30, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-white border border-[#F0EBE3] rounded-3xl w-full max-w-lg p-6 max-h-[90vh] overflow-auto"
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-2xl" style={{ color: accent }}>{title}</h3>
          <button onClick={onClose} className="h-9 w-9 rounded-full bg-[#F4EFE6] hover:bg-[#F0EBE3] flex items-center justify-center">
            <X className="h-4 w-4" />
          </button>
        </div>
        {children}
      </motion.div>
    </motion.div>
  );
}

function EmergencyDialog({ onClose, onDone }) {
  const [dogs, setDogs] = useState([]);
  const [dogId, setDogId] = useState("");
  const [note, setNote] = useState("");
  const [loc, setLoc] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/dogs").then((r) => {
      const real = r.data.filter((d) => !d.is_stub);
      setDogs(real);
      if (real.length) setDogId(real[0].id);
    });
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (p) => setLoc({ lat: p.coords.latitude, lng: p.coords.longitude }),
        () => setLoc({ lat: 41.9028, lng: 12.4964 })
      );
    }
  }, []);

  const submit = async () => {
    if (!dogId) return toast.error("Crea prima un passaporto cane");
    if (!loc) return toast.error("Posizione non disponibile");
    setLoading(true);
    try {
      const { data } = await api.post("/emergencies", {
        dog_id: dogId, lat: loc.lat, lng: loc.lng, last_seen_note: note,
      });
      toast.success(`Allerta diffusa. ${data.notified || 0} utenti notificati. Il raggio crescerà fino a 80 km nei prossimi minuti.`);
      onDone();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal onClose={onClose} title="Allerta emergenza" accent="#FF3B30">
      <p className="text-sm text-[#6B6B6B] mb-4">
        Confermando, il passaporto del tuo cane viene pubblicato sulla mappa. L&apos;allarme parte entro 10 km nei primi 20 minuti, poi si estende fino a 80 km nel tempo (un cane può allontanarsi parecchio).
      </p>
      {dogs.length === 0 ? (
        <div className="text-sm text-[#6B6B6B]">
          Non hai ancora un passaporto.{" "}
          <Link to="/app/cani/nuovo" className="underline">Crealo ora</Link>
        </div>
      ) : (
        <>
          <label className="text-xs uppercase tracking-widest text-[#9A9A9A]">Cane</label>
          <select
            data-testid="em-dog-select"
            value={dogId}
            onChange={(e) => setDogId(e.target.value)}
            className="mt-2 w-full px-4 py-3 rounded-xl bg-[#FAF7F0] border border-[#F0EBE3] outline-none"
          >
            {dogs.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
          <label className="text-xs uppercase tracking-widest text-[#9A9A9A] mt-4 block">Note (opzionale)</label>
          <textarea
            data-testid="em-note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Ultimo avvistamento, area, comportamento..."
            className="mt-2 w-full px-4 py-3 rounded-xl bg-[#FAF7F0] border border-[#F0EBE3] outline-none min-h-[100px]"
          />
          <button
            data-testid="em-submit"
            onClick={submit}
            disabled={loading}
            className="mt-6 w-full px-6 py-3.5 rounded-full bg-[#FF3B30] text-[#1A1A1A] font-semibold hover:bg-[#FF3B30]/90 transition-all disabled:opacity-50"
          >
            {loading ? "Pubblicazione..." : "Diffondi allerta ora"}
          </button>
        </>
      )}
    </Modal>
  );
}

function HazardDialog({ onClose, onDone }) {
  const [type, setType] = useState("poison_bait");
  const [description, setDescription] = useState("");
  const [photo, setPhoto] = useState(null);
  const [loc, setLoc] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (p) => setLoc({ lat: p.coords.latitude, lng: p.coords.longitude }),
        () => {},
        { timeout: 6000 }
      );
    }
  }, []);

  const onPhotoChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 2 * 1024 * 1024) return toast.error("Foto troppo grande (max 2MB)");
    const r = new FileReader();
    r.onload = () => setPhoto(r.result);
    r.readAsDataURL(f);
  };

  const submit = async () => {
    if (!description.trim() || description.trim().length < 5) return toast.error("Descrivi cosa hai visto (min 5 caratteri)");
    if (!loc) return toast.error("Posizione non disponibile — controlla i permessi GPS");
    setLoading(true);
    try {
      const { data } = await api.post("/hazards", {
        type, description, lat: loc.lat, lng: loc.lng, photo_url: photo || null,
      });
      toast.success(`Pericolo segnalato · avvisati ${data.notified} utenti nelle vicinanze`);
      // Karma burst for the reporter (+3 base, +6 if walking — server source of truth)
      try {
        const evt = new CustomEvent("orio:karma", { detail: { delta: 3, reason: "hazard_create" } });
        window.dispatchEvent(evt);
      } catch {/* ignore */}
      onDone();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail));
    } finally { setLoading(false); }
  };

  return (
    <Modal onClose={onClose} title="Segnala un pericolo" accent="#FFB020">
      <p className="text-xs text-[#9A9A9A] -mt-2 mb-3">
        Aiuta gli altri proprietari: la tua segnalazione diventa visibile sulla mappa e invia una notifica
        push a chiunque sia entro 1 km.
      </p>
      <label className="text-[10px] uppercase tracking-widest text-[#9A9A9A]">Tipo</label>
      <div className="mt-2 grid grid-cols-2 gap-2">
        {Object.entries(HAZARD_TYPES).map(([id, v]) => (
          <button key={id} type="button" data-testid={`hz-type-${id}`}
            onClick={() => setType(id)}
            className="px-3 py-2 rounded-xl border text-xs text-left transition-all"
            style={{
              background: type === id ? `${v.color}1a` : "#FAF7F0",
              borderColor: type === id ? `${v.color}60` : "#F0EBE3",
              color: type === id ? v.color : "#1A1A1A",
            }}>
            <span className="mr-1.5">{v.icon}</span>{v.label}
          </button>
        ))}
      </div>
      <Input testid="hz-description" label="Descrizione (cosa, dove esattamente)" value={description} onChange={setDescription} multiline />
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <label className="px-3 py-2 rounded-xl bg-[#F4EFE6] hover:bg-[#F0EBE3] text-xs inline-flex items-center gap-1.5 cursor-pointer">
          <Camera className="h-3.5 w-3.5" /> {photo ? "Cambia foto" : "Aggiungi foto"}
          <input data-testid="hz-photo" type="file" accept="image/*" onChange={onPhotoChange} className="hidden" />
        </label>
        {photo && <span className="text-[11px] text-[#9A9A9A]">Foto pronta</span>}
        {loc && (
          <span className="font-mono text-[10px] text-[#9A9A9A] ml-auto">
            {loc.lat.toFixed(4)}, {loc.lng.toFixed(4)}
          </span>
        )}
      </div>
      <button data-testid="hz-submit" onClick={submit} disabled={loading}
        className="mt-6 w-full px-6 py-3.5 rounded-full bg-[#FFB020] text-black font-semibold transition-all disabled:opacity-50 inline-flex items-center justify-center gap-2">
        <ShieldAlert className="h-4 w-4" /> {loading ? "Pubblicazione..." : "Pubblica segnalazione"}
      </button>
    </Modal>
  );
}

function HazardPopupActions({ hazard, reload }) {
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const isOwner = !!(user?.id && hazard?.reported_by && hazard.reported_by === user.id);
  const act = async (action) => {
    setBusy(true);
    try {
      await api.post(`/hazards/${hazard.id}/${action}`);
      setDone(action);
      toast.success(action === "confirm" ? "Conferma registrata, grazie" : "Segnalato come 'non più presente'");
      if (action === "confirm") {
        // Best-effort optimistic karma burst (+1 base, +2 if walking — server is source of truth)
        try {
          const evt = new CustomEvent("orio:karma", { detail: { delta: 1, reason: "hazard_confirm" } });
          window.dispatchEvent(evt);
        } catch {/* ignore */}
      }
      setTimeout(reload, 800);
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail));
    } finally { setBusy(false); }
  };
  const removeMine = async () => {
    if (!window.confirm("Rimuovere questo pericolo dalla mappa? Sarà eliminato per tutti.")) return;
    setBusy(true);
    try {
      await api.delete(`/hazards/${hazard.id}`);
      toast.success("Pericolo rimosso dalla mappa");
      setDone("removed");
      setTimeout(reload, 500);
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail));
    } finally { setBusy(false); }
  };
  return (
    <div className="mt-3 space-y-1.5">
      <div className="text-[11px] text-[#6B6B6B]">
        Segnalato da <strong>{hazard.reporter_name}</strong>
        {hazard.confirms > 0 && ` · confermato da ${hazard.confirms}`}
      </div>
      {!done && (
        <div className="flex gap-1.5 pt-1">
          {isOwner ? (
            <button data-testid="hz-remove-mine" disabled={busy}
              onClick={removeMine}
              className="flex-1 px-2.5 py-1.5 rounded-lg bg-[#FEE2E2] text-[#E94545] text-[11px] font-medium inline-flex items-center justify-center gap-1 disabled:opacity-50">
              <Trash2 className="h-3 w-3" /> Rimuovi (il tuo)
            </button>
          ) : (
            <>
              <button data-testid="hz-confirm" disabled={busy}
                onClick={() => act("confirm")}
                className="flex-1 px-2.5 py-1.5 rounded-lg bg-[#FFB020]/15 text-[#FFB020] text-[11px] font-medium inline-flex items-center justify-center gap-1 disabled:opacity-50">
                <ThumbsUp className="h-3 w-3" /> Confermo
              </button>
              <button data-testid="hz-dismiss" disabled={busy}
                onClick={() => act("dismiss")}
                className="flex-1 px-2.5 py-1.5 rounded-lg bg-[#F4EFE6] text-[#6B6B6B] text-[11px] font-medium inline-flex items-center justify-center gap-1 disabled:opacity-50">
                <CheckCircle2 className="h-3 w-3" /> Non c&apos;è più
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function Input({ label, value, onChange, type = "text", multiline, testid }) {
  return (
    <div className="mt-3">
      <label className="text-xs uppercase tracking-widest text-[#9A9A9A]">{label}</label>
      {multiline ? (
        <textarea
          data-testid={testid}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="mt-2 w-full px-4 py-3 rounded-xl bg-[#FAF7F0] border border-[#F0EBE3] outline-none min-h-[90px]"
        />
      ) : (
        <input
          data-testid={testid}
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="mt-2 w-full px-4 py-3 rounded-xl bg-[#FAF7F0] border border-[#F0EBE3] outline-none"
        />
      )}
    </div>
  );
}

function SmartSuggestions() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(true);
  useEffect(() => {
    api.get("/suggestions").then((r) => setItems(r.data)).catch(() => {});
  }, []);
  if (items.length === 0 || !open) return null;
  return (
    <div className="absolute top-24 md:top-20 right-2 md:right-4 z-[400] w-[280px] sm:w-[320px] max-h-[60vh] overflow-y-auto no-scrollbar space-y-2">
      <div className="flex items-center justify-between px-1">
        <div className="text-[10px] uppercase tracking-widest text-[#9A9A9A]">Suggerimenti</div>
        <button onClick={() => setOpen(false)} className="text-[#9A9A9A] hover:text-[#1A1A1A] text-xs">✕</button>
      </div>
      {items.slice(0, 3).map((s) => (
        <Link
          key={s.id}
          to={s.action_link || "#"}
          className="block glass rounded-2xl p-3 border hover:bg-[#FAF7F0] transition-colors"
          style={{ borderColor: `${s.color}40` }}
        >
          <div className="flex items-start gap-2.5">
            <span className="mt-1 h-2 w-2 rounded-full shrink-0" style={{ background: s.color, boxShadow: `0 0 10px ${s.color}` }} />
            <div className="flex-1 min-w-0">
              <div className="text-xs font-medium leading-snug">{s.title}</div>
              <div className="text-[11px] text-[#9A9A9A] mt-0.5">{s.body}</div>
              {s.action_label && (
                <div className="mt-1.5 text-[10px] font-medium" style={{ color: s.color }}>{s.action_label} →</div>
              )}
            </div>
          </div>
        </Link>
      ))}
    </div>
  );
}


/* ============================================================
   MapActionsFab — unified action button just above the mobile nav.
   Tap to expand a small floating menu with Segnala / Emergenza /
   Visibilità options. Non-invasive; single button footprint.
   The Visibilità item opens LivePresenceModal (educational sheet
   con illustrazione ORIO, come il Radar).
   ============================================================ */
function MapActionsFab({ onSegnala, onEmergenza, me, onPeersChange, defaultPresenceOpen = false }) {
  const [open, setOpen] = React.useState(false);
  const [presenceOpen, setPresenceOpen] = React.useState(!!defaultPresenceOpen);
  const [visible, setVisible] = React.useState(false);
  const [peers, setPeers] = React.useState([]);
  const loc = Array.isArray(me) ? { lat: me[0], lng: me[1] } : (me && me.lat != null ? me : null);

  return (
    <>
      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[599] bg-black/10 backdrop-blur-[2px] pointer-events-auto"
            onClick={() => setOpen(false)} />
        )}
      </AnimatePresence>

      <div className="absolute top-[264px] right-3 md:right-4 z-[601] pointer-events-none">
        <AnimatePresence>
          {open && (
            <motion.div initial={{ opacity: 0, x: 12, scale: 0.94 }}
              animate={{ opacity: 1, x: 0, scale: 1 }} exit={{ opacity: 0, x: 12, scale: 0.94 }}
              transition={{ duration: 0.22 }}
              className="pointer-events-auto absolute top-1/2 right-full -translate-y-1/2 mr-2.5 flex flex-col gap-2 items-end">
              <ActionMenuItem testid="action-segnala" icon={Camera} label="Segnala in 1 tap"
                bg="linear-gradient(135deg, #FFB020 0%, #FF7A1A 100%)"
                onClick={() => { hapticTap(); setOpen(false); onSegnala?.(); }} />
              <ActionMenuItem testid="action-emergenza" icon={AlertTriangle} label="Emergenza SOS"
                bg="linear-gradient(135deg, #FF3B30 0%, #E11D48 100%)"
                onClick={() => { hapticTap(); setOpen(false); onEmergenza?.(); }} />
              <ActionMenuItem testid="action-visibility"
                icon={visible ? Eye : EyeOff}
                label={visible ? `Chi c'è online · ${peers.length}` : "Chi c'è online"}
                bg={visible ? "linear-gradient(135deg, #34D399 0%, #2DBA60 100%)" : "linear-gradient(135deg, #F5EFE6 0%, #EDE6D9 100%)"}
                labelColor={visible ? "#fff" : "#4A4A4A"}
                onClick={() => { hapticTap(); setOpen(false); setPresenceOpen(true); }} />
            </motion.div>
          )}
        </AnimatePresence>

        <button data-testid="map-actions-fab"
          onClick={() => { hapticTap(); setOpen((v) => !v); }}
          className="pointer-events-auto h-11 w-11 rounded-full flex items-center justify-center transition-all active:scale-95"
          style={{
            background: open ? "linear-gradient(135deg, #7A7A7A 0%, #4A4A4A 100%)" : "linear-gradient(135deg, #FF7A1A 0%, #FF5C2A 100%)",
            boxShadow: open ? "0 6px 16px -6px rgba(0,0,0,0.4)" : "0 8px 20px -6px rgba(255,122,26,0.55)",
            border: "2px solid rgba(255,255,255,0.92)",
          }}
          aria-label={open ? "Chiudi menu" : "Apri menu azioni"}>
          {open ? <X className="h-4 w-4 text-white" /> : <Zap className="h-4 w-4 text-white" strokeWidth={2.5} />}
        </button>
      </div>

      {/* Live presence educational + interactive modal (portal to body). */}
      <LivePresenceModal
        open={presenceOpen}
        onClose={() => setPresenceOpen(false)}
        me={loc}
        onVisibleChange={setVisible}
        onPeersChange={(items) => { setPeers(items || []); onPeersChange?.(items || []); }}
      />
    </>
  );
}

function ActionMenuItem({ icon: Icon, label, bg, labelColor = "#fff", onClick, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className="w-[230px] flex items-center gap-3 px-4 py-3 rounded-2xl backdrop-blur transition-all active:scale-95"
      style={{ background: bg, color: labelColor, boxShadow: "0 10px 24px -8px rgba(0,0,0,0.25)", border: "1px solid rgba(255,255,255,0.7)" }}>
      <span className="h-8 w-8 rounded-full flex items-center justify-center shrink-0"
        style={{ background: labelColor === "#fff" ? "rgba(255,255,255,0.22)" : "rgba(255,255,255,0.9)" }}>
        <Icon className="h-4 w-4" />
      </span>
      <span className="flex-1 text-left text-[13px] font-semibold leading-tight">{label}</span>
    </button>
  );
}
